import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useStateContext } from "contexts/ContextProvider";
import linguistApi from "services/linguistApi";

const LinguistProfileEditOverlay = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar, triggerPageRefresh } = useStateContext();

  const [name, setName] = useState(overlayData?.profile?.Name || "");
  const [email, setEmail] = useState(overlayData?.profile?.Email || "");
  const [phoneNumber, setPhoneNumber] = useState(
    overlayData?.profile?.PhoneNumber || "",
  );
  const [addressLine1, setAddressLine1] = useState(
    overlayData?.profile?.AddressLine1 || "",
  );
  const [addressLine2, setAddressLine2] = useState(
    overlayData?.profile?.AddressLine2 || "",
  );
  const [addressLine3, setAddressLine3] = useState(
    overlayData?.profile?.AddressLine3 || "",
  );
  const [certifications, setCertifications] = useState(
    overlayData?.profile?.Certifications || "",
  );
  const [isSaving, setIsSaving] = useState(false);

  const canSave = useMemo(
    () => Boolean(name.trim() && email.trim()) && !isSaving,
    [name, email, isSaving],
  );

  const closeOverlay = () => setOverlayName("");

  const handleSave = async () => {
    if (!name.trim() || !email.trim()) {
      showSnackbar("Name and email are required", theme, "error");
      return;
    }

    setIsSaving(true);
    try {
      await linguistApi.put("/me", {
        Name: name.trim(),
        Email: email.trim(),
        PhoneNumber: phoneNumber.trim(),
        AddressLine1: addressLine1.trim(),
        AddressLine2: addressLine2.trim(),
        AddressLine3: addressLine3.trim(),
        Certifications: certifications.trim(),
      });

      triggerPageRefresh("Profile updated", theme, "success");
      closeOverlay();
    } catch (error) {
      showSnackbar(
        error?.response?.data?.error || "Unable to save profile right now",
        theme,
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      p={isMobile ? "1rem" : "2rem"}
      bgcolor={theme.palette.background.alt}
      sx={{
        height: "100%",
        overflow: "auto",
      }}
    >
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={closeOverlay}>
              <ChevronLeftRoundedIcon />
            </IconButton>
            <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold">
              Edit Profile
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={
              isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />
            }
            onClick={handleSave}
            disabled={!canSave}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>

        <Box
          sx={{
            bgcolor: theme.palette.background.default,
            borderRadius: "12px",
            border: `1px solid ${theme.palette.primary.border2}`,
            boxShadow: theme.shadows[3],
            p: 2,
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Phone Number"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Certifications"
                value={certifications}
                onChange={(event) => setCertifications(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Address Line 1"
                value={addressLine1}
                onChange={(event) => setAddressLine1(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Address Line 2"
                value={addressLine2}
                onChange={(event) => setAddressLine2(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Address Line 3"
                value={addressLine3}
                onChange={(event) => setAddressLine3(event.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default LinguistProfileEditOverlay;
